import { messages } from '../messages';
import {
  SearchInterpretationResponse,
  SearchInterpretation,
  SearchLink
} from 'Src/types/search';
import { isSearchRefined } from 'Src/utils/search-common';
import { getQueryParams } from 'Src/utils/url';

/**
 * Determines the appropriate message descriptor and test ID for search summary
 * @param {string} queryString - The search query string
 * @param {boolean} isMapSearch - Whether the search is a map-type search
 * @param {boolean} showSearchDescription - Whether to show search description
 * @returns {Object} Object containing messageDescriptor and testId
 */
export function getSearchSummaryMessage(
  queryString: string,
  isMapSearch: boolean,
  showSearchDescription: boolean
) {
  const queryParams = getQueryParams(queryString);
  const shouldShowRefinedSearch = isSearchRefined(queryString);

  if (isMapSearch === true) {
    if (showSearchDescription) {
      return {
        messageDescriptor: messages.foundfor,
        testId: 'messages-foundfor'
      };
    }
    return {
      messageDescriptor: messages.found,
      testId: 'messages-found'
    };
  }

  if (showSearchDescription && queryParams.location != null) {
    return {
      messageDescriptor: messages.foundfornear,
      testId: 'messages-foundfornear'
    };
  } else if (showSearchDescription && shouldShowRefinedSearch) {
    return {
      messageDescriptor: messages.foundForRefined,
      testId: 'messages-foundfor-refined'
    };
  } else if (showSearchDescription) {
    return {
      messageDescriptor: messages.foundfor,
      testId: 'messages-foundfor'
    };
  } else if (queryParams.location != null) {
    return {
      messageDescriptor: messages.foundnear,
      testId: 'messages-foundnear'
    };
  } else {
    return {
      messageDescriptor: messages.found,
      testId: 'messages-found'
    };
  }
}

const LINKABLE_SEARCH_CATEGORIES = ['clinical_keywords', 'specialties'];

interface GetInterpretationSearchLinksParams {
  interpretation: SearchInterpretationResponse;
  searchParams: Record<string, any>;
  searchType: string;
}
/**
 * Transforms the interpretation from the search service into a format suitable for building links
 *
 * @param {SearchInterpretation[]} interpretation from Search Service
 * @returns {SearchLink[]} An array of labels with the query modifications needed to link to a new search
 */
export function getInterpretationSearchLinks({
  interpretation = [],
  searchParams,
  searchType
}: GetInterpretationSearchLinksParams): SearchLink[] {
  // TODO update this to check the `semantic_search_used` message on the response once it exists
  // instead of looking at the params https://healthsparq.atlassian.net/browse/HSAP-4159
  if (searchParams.use_knowledge_graph_results && searchType === 'unified') {
    // Each category will only ever appear once in the interpretation
    const flattenedInterpretation: SearchInterpretation = Object.assign(
      {},
      ...interpretation
    );

    return LINKABLE_SEARCH_CATEGORIES.flatMap((category) =>
      (flattenedInterpretation[category] ?? []).map(
        (term): SearchLink => ({
          display_text: term,
          display_search_type: category,
          query_modifications: [
            { action: 'delete_key', key: 'unified' },
            // Clear any filters except location to prevent returning no results
            { action: 'delete_key', key: 'filter' },
            { action: 'append', key: category, value: term }
          ]
        })
      )
    );
  }
  return [];
}
